import { Link, useLocation } from "wouter";
import { useAuth } from "@/lib/auth";
import { usePricingVisibility } from "@/lib/pricing-visibility";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { HelpCircle, User, Settings, Menu, X, BarChart3, DollarSign } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useState, useEffect } from "react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { isPricingVisible, togglePricing } = usePricingVisibility();
  const [location] = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path: string) => location === path;

  // Close mobile menu when location changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Close mobile menu on window resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm border-b border-neutral-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Link href="/" aria-label="MAX - Home">
                <img 
                  src="https://max.live/hubfs/MAX-Logo-color-dark72.png" 
                  alt="MAX - Music Sponsorship Platform" 
                  className="h-8 sm:h-10 w-auto cursor-pointer hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-max-brand focus:ring-offset-2"
                />
              </Link>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:ml-8 md:flex items-center space-x-8">
              <Link href="/browse-artists" className={`px-3 py-2 text-sm font-medium flex items-center h-10 ${
                isActive("/browse-artists") || isActive("/browse-venues") || isActive("/browse-markets") ? "text-primary" : "text-neutral-500 hover:text-primary"
              }`}>
                Browse
              </Link>
              <Link href="/plans" className={`px-3 py-2 text-sm font-medium flex items-center h-10 ${
                isActive("/plans") ? "text-primary" : "text-neutral-500 hover:text-primary"
              }`}>
                Plan
              </Link>
            </nav>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Desktop right navigation - Calculator */}
            <nav className="hidden md:flex items-center space-x-6">
              <Link href="/calculator" className={`px-3 py-2 text-sm font-medium flex items-center h-10 ${
                isActive("/calculator") ? "text-primary" : "text-neutral-500 hover:text-primary"
              }`}>
                Calculator
              </Link>
            </nav>
            
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" />
              )}
            </Button>

            <Button 
              asChild
              variant="ghost" 
              size="icon" 
              className="hidden sm:flex text-neutral-500 hover:text-primary"
              aria-label="Help Center"
            >
              <Link href="/help-center">
                <HelpCircle className="h-5 w-5" aria-hidden="true" />
              </Link>
            </Button>
            
            {user ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center space-x-2"
                      aria-label={`User menu for ${user.name}`}
                      aria-haspopup="menu"
                    >
                      <Avatar className="h-8 w-8">
                        {user.profileImageUrl && !user.profileImageUrl.startsWith('blob:') ? (
                          <AvatarImage 
                            src={user.profileImageUrl} 
                            alt={`${user.name}'s profile picture`}
                            onError={(e) => {
                              // Hide image on error, fallback to initials
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : null}
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {user.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden sm:block text-sm font-medium text-neutral-900">{user.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link href="/profile" className="flex items-center">
                        <User className="mr-2 h-4 w-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        togglePricing();
                      }}
                      className="flex items-center justify-between cursor-pointer"
                    >
                      <div className="flex items-center">
                        <DollarSign className="mr-2 h-4 w-4" />
                        Show Pricing
                      </div>
                      <Switch
                        checked={isPricingVisible}
                        onCheckedChange={togglePricing}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </DropdownMenuItem>
                    {(user.role === 'admin' || user.role === 'super_admin') && (
                      <DropdownMenuItem asChild>
                        <Link href="/admin" className="flex items-center">
                          <Settings className="mr-2 h-4 w-4" />
                          Admin Dashboard
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout}>
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <Button asChild variant="ghost" size="sm">
                  <Link href="/login">Login</Link>
                </Button>
                <Button asChild size="sm">
                  <Link href="/register">Sign Up</Link>
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <nav 
            className="md:hidden border-t border-neutral-200 bg-white" 
            id="mobile-navigation"
            aria-label="Mobile navigation menu"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link 
                href="/browse-artists" 
                className={`block px-3 py-2 text-base font-medium rounded-md ${
                  isActive("/browse-artists") || isActive("/browse-venues") || isActive("/browse-markets") 
                    ? "text-primary bg-primary/10" 
                    : "text-neutral-700 hover:text-primary hover:bg-neutral-50"
                }`}
              >
                Browse
              </Link>

              <Link
                href="/plans"
                className={`block px-3 py-2 text-base font-medium rounded-md ${
                  isActive("/plans") ? "text-primary bg-primary/10" : "text-neutral-700 hover:text-primary hover:bg-neutral-50"
                }`}
              >
                Plan
              </Link>

              <Link
                href="/calculator"
                className={`block px-3 py-2 text-base font-medium rounded-md ${
                  isActive("/calculator") ? "text-primary bg-primary/10" : "text-neutral-700 hover:text-primary hover:bg-neutral-50"
                }`}
              >
                Calculator
              </Link>
              
              <Link 
                href="/help-center" 
                className={`block px-3 py-2 text-base font-medium rounded-md ${
                  isActive("/help-center") ? "text-primary bg-primary/10" : "text-neutral-700 hover:text-primary hover:bg-neutral-50"
                }`}
              >
                Help Center
              </Link>

              {/* Mobile User Menu */}
              {user && (
                <div className="pt-4 border-t border-neutral-200 mt-3">
                  <div className="flex items-center px-3 py-2">
                    <Avatar className="h-10 w-10">
                      {user.profileImageUrl && !user.profileImageUrl.startsWith('blob:') ? (
                        <AvatarImage 
                          src={user.profileImageUrl} 
                          alt={user.name}
                        />
                      ) : null}
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {user.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="ml-3">
                      <div className="text-base font-medium text-neutral-800">{user.name}</div>
                      <div className="text-sm text-neutral-500">{user.email}</div>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1">
                    <Link
                      href="/profile"
                      className="block px-3 py-2 text-base font-medium text-neutral-700 hover:text-primary hover:bg-neutral-50 rounded-md"
                    >
                      Profile
                    </Link>
                    <div className="flex items-center justify-between px-3 py-2 text-base font-medium text-neutral-700 rounded-md">
                      <div className="flex items-center">
                        <DollarSign className="mr-2 h-5 w-5" />
                        Show Pricing
                      </div>
                      <Switch
                        checked={isPricingVisible}
                        onCheckedChange={togglePricing}
                      />
                    </div>
                    {(user.role === 'admin' || user.role === 'super_admin') && (
                      <Link 
                        href="/admin" 
                        className="block px-3 py-2 text-base font-medium text-neutral-700 hover:text-primary hover:bg-neutral-50 rounded-md"
                      >
                        Admin Dashboard
                      </Link>
                    )}
                    <button
                      onClick={logout}
                      className="block w-full text-left px-3 py-2 text-base font-medium text-neutral-700 hover:text-primary hover:bg-neutral-50 rounded-md"
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
